/**
 * Layout paths for curved text, parameterised by arc length.
 *
 * Every path maps a signed distance along the curve (0 = the anchor the run is centred on) to a
 * point and the tangent there. Placing glyphs by arc length is what keeps letter gaps even: a
 * glyph's advance is a length along the baseline, so stepping along x instead (as a plain
 * `y = f(x)` layout does) stretches the gaps wherever the curve is steep.
 */

/** A point on a layout path; `rotation` is the tangent in degrees, clockwise in SVG space. */
export type PathPoint = { x: number; y: number; rotation: number };

/** Signed arc length from the anchor → point on the path. */
export type CurvePath = (offset: number) => PathPoint;

const DEG = 180 / Math.PI;

/**
 * Circular arch whose apex sits at (cx, cy). `direction` 1 arches up (ends drop), -1 down.
 * Arc length is exact: theta = offset / radius.
 */
export function arcPath(cx: number, cy: number, radius: number, direction: 1 | -1): CurvePath {
  const r = Math.max(1e-6, radius);
  return (offset) => {
    const theta = offset / r;
    return {
      x: cx + r * Math.sin(theta),
      y: cy + direction * r * (1 - Math.cos(theta)),
      rotation: direction * theta * DEG,
    };
  };
}

/** Straight baseline through (cx, cy) tilted by `slope` degrees (positive rises to the right). */
export function linePath(cx: number, cy: number, slope: number): CurvePath {
  const radians = slope / DEG;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return (offset) => ({ x: cx + offset * cos, y: cy - offset * sin, rotation: -slope });
}

type Sample = { s: number; x: number; y: number; slope: number };

/**
 * Lookup table for a parametric curve sampled densely enough that linear interpolation between
 * samples is well under a tenth of a pixel for text-sized curves.
 */
function tablePath(samples: Sample[], anchor: number): CurvePath {
  const last = samples.length - 1;
  const at = (s: number): PathPoint => {
    if (s <= samples[0].s || s >= samples[last].s) {
      // Past either end, continue along the end tangent so outer glyphs never pile up.
      const end = s <= samples[0].s ? samples[0] : samples[last];
      const angle = Math.atan(end.slope);
      const run = s - end.s;
      return { x: end.x + run * Math.cos(angle), y: end.y + run * Math.sin(angle), rotation: angle * DEG };
    }
    let low = 0;
    let high = last;
    while (high - low > 1) {
      const middle = (low + high) >> 1;
      if (samples[middle].s <= s) low = middle;
      else high = middle;
    }
    const a = samples[low];
    const b = samples[high];
    const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
    const slope = a.slope + (b.slope - a.slope) * t;
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, rotation: Math.atan(slope) * DEG };
  };
  return (offset) => at(anchor + offset);
}

function sampleFunction(
  fromX: number,
  toX: number,
  count: number,
  y: (x: number) => number,
  dy: (x: number) => number,
): { samples: Sample[]; length: number } {
  const samples: Sample[] = [];
  let s = 0;
  let prevX = fromX;
  let prevY = y(fromX);
  for (let index = 0; index <= count; index += 1) {
    const x = fromX + (toX - fromX) * index / count;
    const value = y(x);
    if (index > 0) s += Math.hypot(x - prevX, value - prevY);
    samples.push({ s, x, y: value, slope: dy(x) });
    prevX = x;
    prevY = value;
  }
  return { samples, length: s };
}

/** Arc length of one sine span of width `span` (Simpson's rule on the unit interval). */
function sineLength(span: number, amplitude: number, cycles: number, phase: number): number {
  const steps = 256;
  const k = amplitude * 2 * Math.PI * cycles;
  const f = (u: number) => Math.sqrt(span * span + (k * Math.cos(2 * Math.PI * cycles * u + phase)) ** 2);
  let sum = f(-0.5) + f(0.5);
  for (let index = 1; index < steps; index += 1) {
    sum += f(-0.5 + index / steps) * (index % 2 ? 4 : 2);
  }
  return sum / (3 * steps);
}

const waveCache = new Map<string, number>();

/**
 * Horizontal span whose sine wave is exactly `length` long, so the text spans `cycles` waves.
 * Never narrower than a third of the text: a tiny run on a tall, fast wave would otherwise stack
 * its letters vertically; past that point the run simply covers the middle of the wave.
 */
function waveSpan(length: number, amplitude: number, cycles: number, phase: number): number {
  const key = `${length.toFixed(3)}|${amplitude}|${cycles}|${phase.toFixed(5)}`;
  const known = waveCache.get(key);
  if (known !== undefined) return known;
  const min = length / 3;
  let span = min;
  if (sineLength(min, amplitude, cycles, phase) < length) {
    let low = min;
    let high = length;
    for (let iteration = 0; iteration < 40; iteration += 1) {
      const middle = (low + high) / 2;
      if (sineLength(middle, amplitude, cycles, phase) < length) low = middle;
      else high = middle;
    }
    span = (low + high) / 2;
  }
  if (waveCache.size > 256) waveCache.clear();
  waveCache.set(key, span);
  return span;
}

/**
 * Sine wave through (cx, cy): `y = cy + amplitude·sin(2π·cycles·x/span + phase)`, where the span
 * is solved so the curve is as long as the text (`length`). The anchor is x = 0.
 */
export function wavePath(cx: number, cy: number, amplitude: number, cycles: number, phase: number, length: number): CurvePath {
  if (Math.abs(amplitude) < 1e-6 || length <= 0) return linePath(cx, cy, 0);
  const span = waveSpan(length, amplitude, cycles, phase);
  const omega = 2 * Math.PI * cycles / span;
  const y = (x: number) => cy + amplitude * Math.sin(omega * x + phase);
  const dy = (x: number) => amplitude * omega * Math.cos(omega * x + phase);
  // ~8 samples per pixel of the steepest stretch keeps interpolation error sub-pixel.
  const count = Math.min(4096, Math.max(256, Math.ceil(length * 2 + cycles * 128)));
  const { samples } = sampleFunction(-span / 2, span / 2, count, (x) => y(x), dy);
  for (const sample of samples) sample.x += cx;
  const anchorIndex = count >> 1;
  return tablePath(samples, samples[anchorIndex].s);
}

/**
 * Ring (circle or ellipse) centred on (cx, cy), anchored at its top or bottom point.
 *
 * Text reads upright at the anchor: from the top it runs clockwise, from the bottom
 * counter-clockwise, so both read left-to-right on screen. Whether the ink sits outside or inside
 * the ring is a baseline shift, not a flip (see `placeRun`). Ellipses are arc-length
 * parameterised through a lookup table and glyphs follow the true tangent, which on an ellipse is
 * not the polar angle.
 */
export function ringPath(cx: number, cy: number, radiusX: number, radiusY: number, bottom: boolean): CurvePath {
  const rx = Math.max(1e-6, radiusX);
  const ry = Math.max(1e-6, radiusY);
  const travel = bottom ? -1 : 1;
  const anchorT = bottom ? Math.PI : 0;
  const flip = bottom ? 180 : 0;
  // t runs clockwise from 12 o'clock: P(t) = (cx + rx·sin t, cy − ry·cos t).
  const pointAt = (t: number): PathPoint => ({
    x: cx + rx * Math.sin(t),
    y: cy - ry * Math.cos(t),
    rotation: Math.atan2(ry * Math.sin(t), rx * Math.cos(t)) * DEG + flip,
  });
  if (Math.abs(rx - ry) < 1e-6) return (offset) => pointAt(anchorT + travel * offset / rx);

  const count = 2048;
  const ts: number[] = [];
  const lengths: number[] = [];
  let length = 0;
  let prev = pointAt(anchorT - Math.PI);
  for (let index = 0; index <= count; index += 1) {
    const t = anchorT - Math.PI + 2 * Math.PI * index / count;
    const current = pointAt(t);
    if (index > 0) length += Math.hypot(current.x - prev.x, current.y - prev.y);
    ts.push(t);
    lengths.push(length);
    prev = current;
  }
  const anchor = lengths[count >> 1];
  return (offset) => {
    const target = Math.max(0, Math.min(length, anchor + travel * offset));
    let low = 0;
    let high = count;
    while (high - low > 1) {
      const middle = (low + high) >> 1;
      if (lengths[middle] <= target) low = middle;
      else high = middle;
    }
    const span = lengths[high] - lengths[low];
    const t = ts[low] + (ts[high] - ts[low]) * (span > 0 ? (target - lengths[low]) / span : 0);
    return pointAt(t);
  };
}
